// AI-ASSISTED: generated with claude-code on 2026-06-03, reviewed by Yoann Hertienne
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Entity } from '@backstage/catalog-model';
import { TileBoundary } from './TileBoundary';

type Annotations = NonNullable<Entity['metadata']['annotations']>;

const ann = (a: Annotations | undefined, key: string) => (a ? a[key] : undefined);

type TileProps = { component: Entity };

const OwnerTile = ({ component }: TileProps) => {
  const owner = component.spec?.owner as string | undefined;
  if (!owner) return <Typography variant="body2">Unowned</Typography>;
  const slug = owner.replace(/^group:default\//, '').replace(/^group:/, '');
  return (
    <Link href={`/catalog/default/group/${slug}`}>
      <Chip size="small" label={`Owner: ${slug}`} />
    </Link>
  );
};

const RepoTile = ({ component }: TileProps) => {
  const src = ann(component.metadata.annotations, 'backstage.io/source-location');
  if (!src) return <Typography variant="body2">No repo</Typography>;
  const url = src.replace(/^url:/, '');
  return (
    <Link href={url} target="_blank" rel="noreferrer" aria-label="Source repository">
      Repo
    </Link>
  );
};

const AdoTile = ({ component }: TileProps) => {
  const project = ann(component.metadata.annotations, 'dev.azure.com/project');
  const buildDef = ann(component.metadata.annotations, 'dev.azure.com/build-definition');
  const org = ann(component.metadata.annotations, 'dev.azure.com/org') ?? 'LuminusITDigital';
  if (!project || !buildDef)
    return <Typography variant="body2">No ADO pipeline</Typography>;
  const url = `https://dev.azure.com/${org}/${project}/_build?definitionName=${encodeURIComponent(
    buildDef,
  )}`;
  return (
    <Link href={url} target="_blank" rel="noreferrer" aria-label="Azure DevOps pipeline">
      ADO pipeline
    </Link>
  );
};

const SonarTile = ({ component }: TileProps) => {
  const key = ann(component.metadata.annotations, 'sonarqube.org/project-key');
  if (!key) return <Typography variant="body2">No SonarCloud project</Typography>;
  const url = `https://sonarcloud.io/summary/overall?id=${encodeURIComponent(key)}`;
  return (
    <Link href={url} target="_blank" rel="noreferrer" aria-label="SonarCloud quality gate">
      Quality gate
    </Link>
  );
};

const TechDocsTile = ({ component }: TileProps) => {
  const ref = ann(component.metadata.annotations, 'backstage.io/techdocs-ref');
  if (!ref) return <Typography variant="body2">No docs</Typography>;
  const ns = component.metadata.namespace ?? 'default';
  return (
    <Link
      href={`/docs/${ns}/component/${component.metadata.name}`}
      aria-label="TechDocs"
    >
      TechDocs
    </Link>
  );
};

export const ComponentTile = ({ component }: TileProps) => {
  const title = component.metadata.title ?? component.metadata.name;
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Stack spacing={1.5}>
          <Box>
            <Link href={`/catalog/default/component/${component.metadata.name}`}>
              <Typography variant="h6">{title}</Typography>
            </Link>
            {component.metadata.description && (
              <Typography variant="body2" color="text.secondary">
                {component.metadata.description}
              </Typography>
            )}
          </Box>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            <TileBoundary tool="Owner">
              <OwnerTile component={component} />
            </TileBoundary>
            <TileBoundary tool="Repo">
              <RepoTile component={component} />
            </TileBoundary>
            <TileBoundary tool="Azure DevOps">
              <AdoTile component={component} />
            </TileBoundary>
            <TileBoundary tool="SonarCloud">
              <SonarTile component={component} />
            </TileBoundary>
            <TileBoundary tool="TechDocs">
              <TechDocsTile component={component} />
            </TileBoundary>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};
